/**
 * Transport + cache for the caller's own org Product entitlements, Axis 1
 * (product-entitlement-enforcement Task 4, Req 3.3, 3.4).
 *
 * WHY THIS IS A SEPARATE MODULE FROM THE HOOK.
 * The cache has to be invalidated from `AuthContext.handleSwitchTenant`, because
 * entitlements are PER-TENANT and a cache surviving a switch would let one
 * organization's subscriptions gate another's surfaces. If the invalidator lived
 * in `useMyEntitlements`, that import would create a cycle:
 *
 *   AuthContext -> useMyEntitlements -> useAuth -> AuthContext
 *
 * Nothing in that cycle dereferences a binding during module evaluation today, so
 * it would probably work — but "probably works" is a poor property for an auth
 * context, and any future module-scope access on either side would break it
 * subtly. Keeping the cache in a LEAF module (no React, no AuthContext) removes
 * the cycle outright, and separates transport from the React binding.
 *
 * The query string is defined locally because the web codegen client does not
 * include it (the schema is hand-mirrored), matching `useMyMemberships`.
 */
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

import {
  NO_PRODUCT_SUBSCRIPTIONS,
  PRODUCT_CATALOG,
  type OrgProductSubscriptions,
} from '@/lib/accessCapabilities';

const MY_ENTITLEMENTS = /* GraphQL */ `
  query MyEntitlements {
    myEntitlements {
      success
      tenantId
      message
      entitlements {
        podium
        director
        merit
        envoy
        patron
        spotlight
        vault
      }
    }
  }
`;

/** The wire shape: lowercase flag keys, matching the stored row's own keys. */
export interface WireEntitlements {
  podium: boolean;
  director: boolean;
  merit: boolean;
  envoy: boolean;
  patron: boolean;
  spotlight: boolean;
  vault: boolean;
}

interface MyEntitlementsData {
  myEntitlements: {
    success: boolean;
    tenantId: string | null;
    message: string;
    entitlements: WireEntitlements | null;
  } | null;
}

export interface ResolvedEntitlements {
  subscriptions: OrgProductSubscriptions;
  tenantId: string | null;
  /** False when the READ failed, as opposed to a tenant that enables nothing. */
  ok: boolean;
}

const client = generateClient();

let entitlementsPromise: Promise<ResolvedEntitlements> | null = null;

/**
 * Map the lowercase wire flags onto the TitleCase `Product` keys the shared
 * evaluator consumes.
 *
 * Folds over `PRODUCT_CATALOG` rather than over the response's own keys, so a
 * Product can never be silently missing and an unexpected extra field cannot
 * introduce one. A flag grants ONLY on exactly boolean `true`, mirroring the
 * server resolver so client and server agree on what a malformed value means.
 */
export function toSubscriptions(wire: WireEntitlements | null): OrgProductSubscriptions {
  if (!wire) return NO_PRODUCT_SUBSCRIPTIONS;
  const source = wire as unknown as Record<string, unknown>;
  const out: Record<string, boolean> = { ...NO_PRODUCT_SUBSCRIPTIONS };
  for (const product of PRODUCT_CATALOG) {
    out[product] = source[product.toLowerCase()] === true;
  }
  return out as OrgProductSubscriptions;
}

/**
 * Fetch (or reuse the in-flight/resolved) entitlements. One round trip is shared
 * across concurrent mounts.
 */
export function fetchMyEntitlements(): Promise<ResolvedEntitlements> {
  if (!entitlementsPromise) {
    entitlementsPromise = (
      client.graphql({
        query: MY_ENTITLEMENTS,
        authMode: 'userPool',
      }) as Promise<GraphQLResult<MyEntitlementsData>>
    )
      .then((res) => {
        const payload = res.data?.myEntitlements ?? null;
        return {
          subscriptions: toSubscriptions(payload?.entitlements ?? null),
          tenantId: payload?.tenantId ?? null,
          // A missing payload is a failed read, not an empty subscription set.
          ok: payload?.success === true,
        };
      })
      .catch((err) => {
        // Bust the cache so the next mount re-attempts rather than permanently
        // serving a rejected promise.
        entitlementsPromise = null;
        throw err;
      });
  }
  return entitlementsPromise;
}

/**
 * Bust the cached `myEntitlements` promise.
 *
 * MUST be called on tenant switch — see the module header.
 */
export function invalidateMyEntitlementsCache(): void {
  entitlementsPromise = null;
}
